import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2,
  ArrowDownRight
} from 'lucide-react';

import AccountTab from './account/AccountTab';
import NotificationsTab from './notifications/NotificationsTab';
import SubscriptionTab from './subscription/SubscriptionTab';
import BillingTab from './billing/BillingTab';
import InvoicesTab from './invoices/InvoicesTab';
import CompanyTab from './company/CompanyTab';
import ApisTab from './api/ApisTab';
import * as settingsApi from './endpoints';
import {
  PLANS,
  TABS,
  INITIAL_ACCOUNT_FORM,
  INITIAL_PAYMENT_FORM,
  INITIAL_INVOICING_FORM,
  INITIAL_FISCAL_FORM,
  INITIAL_API_CONNECTIONS,
  INITIAL_NOTIFICATIONS
} from './consts';
// Pricing plans

export function Settings() {
  const { t } = useTranslation();
  const { updatePlan, user } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState('');

  // Account form state
  const [accountForm, setAccountForm] = useState(INITIAL_ACCOUNT_FORM);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState(INITIAL_PAYMENT_FORM);

  // Invoicing form state
  const [invoicingForm, setInvoicingForm] = useState(INITIAL_INVOICING_FORM);

  // Fiscal form state
  const [fiscalForm, setFiscalForm] = useState(INITIAL_FISCAL_FORM);

  // API Connections state
  const [apiConnections, setApiConnections] = useState(INITIAL_API_CONNECTIONS);

  // Company logo state
  const [companyLogo, setCompanyLogo] = useState(null);

  // Email notification settings state
  const [notifications, setNotifications] = useState(INITIAL_NOTIFICATIONS);

  // Real invoices from DB
  const [invoices, setInvoices] = useState([]);

  // Subscription usage from DB (null means not yet loaded / not available)
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);

  const currentPlan = user?.plan || null;
  const currentPlanData = currentPlan ? PLANS.find(p => p.id === currentPlan) : null;

  useEffect(() => {
    if (user) {
      setAccountForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        company: user.company || ''
      }));
    }
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadSettings = async () => {
    try {
      const data = await settingsApi.getSettings(user.id);
      if (data) {
        const { payment, invoicing, fiscal, api_connections, company_logo, userInfo, subscription } = data;
        if (payment) setPaymentForm(prev => ({ ...prev, ...payment }));
        if (invoicing) setInvoicingForm(prev => ({ ...prev, ...invoicing }));
        if (fiscal) setFiscalForm(prev => ({ ...prev, ...fiscal }));
        if (company_logo) setCompanyLogo(company_logo);
        if (userInfo) {
          setAccountForm(prev => ({
            ...prev,
            firstName: userInfo.name || '',
            lastName: userInfo.last_name || '',
            phone: userInfo.phone_number || '',
          }));
        }
        if (api_connections) {
          setApiConnections(prev => prev.map(conn => {
            const saved = api_connections.find(c => c.id === conn.id);
            return saved ? { ...conn, ...saved } : conn;
          }));
        }
        // subscription contains usage counters and billing info from the DB
        if (subscription) setSubscriptionUsage(subscription);
      }


      // Load notification settings
      const notifData = await settingsApi.getNotifications();
      if (notifData) {
        setNotifications(notifData);
      }

      // Load invoices
      const invoicesData = await settingsApi.getInvoices();
      if (invoicesData) {
        setInvoices(invoicesData);
      }
    } catch (error) {
      // Settings might not exist yet
    }
  };



  const handleChangePlan = async () => {
    if (!selectedNewPlan) return;
    setSaving(true);
    try {
      await updatePlan(selectedNewPlan);
      toast.success(`Successfully changed to ${PLANS.find(p => p.id === selectedNewPlan)?.name} plan`);
      setShowChangePlanModal(false);
      setSelectedNewPlan('');
    } catch (error) {
      toast.error('Failed to change plan');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    setSaving(true);
    try {
      // Mock cancellation
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Subscription cancelled. You can continue using the service until the end of your billing period.');
      setShowCancelModal(false);
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setSaving(false);
    }
  };



  const updateApiKey = (connectionId, apiKey) => {
    setApiConnections(prev => prev.map(c =>
      c.id === connectionId ? { ...c, apiKey } : c
    ));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('settings.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('settings.subtitle')}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 md:grid-cols-7 gap-2 h-auto p-1 bg-muted/50">
          {TABS.map(tab => {
            const tabKeyMap = {
              account: 'navAccount',
              company: 'navCompany',
              subscription: 'navSubscription',
              billing: 'navBilling',
              invoices: 'navInvoicing',
              apis: 'navApi',
              notifications: 'navNotifications',
            };
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex items-center gap-2 py-2.5 data-[state=active]:bg-background"
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden md:inline text-xs">{t(`settings.${tabKeyMap[tab.id]}`)}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Account Tab */}
        <TabsContent value="account" className="space-y-6">
          <AccountTab
            accountForm={accountForm}
            setAccountForm={setAccountForm}
            showNewPassword={showNewPassword}
          />
        </TabsContent>

        {/* Company Tab */}
        <TabsContent value="company" className="space-y-6">
          <CompanyTab
            companyLogo={companyLogo}
            setCompanyLogo={setCompanyLogo}
            fiscalForm={fiscalForm}
            setFiscalForm={setFiscalForm}
          />
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <NotificationsTab
            notifications={notifications}
            setNotifications={setNotifications}
          />
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription" className="space-y-6">
          <SubscriptionTab
            currentPlan={currentPlan}
            currentPlanData={currentPlanData}
            plans={PLANS}
            subscriptionUsage={subscriptionUsage}
            setShowChangePlanModal={setShowChangePlanModal}
            setShowCancelModal={setShowCancelModal}
            setSelectedNewPlan={setSelectedNewPlan}
          />
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          <BillingTab
            currentPlan={currentPlan}
            formatDate={formatDate}
          />
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-6">
          <InvoicesTab
            invoicingForm={invoicingForm}
            setInvoicingForm={setInvoicingForm}
            invoices={invoices}
            formatDate={formatDate}
          />
        </TabsContent>

        {/* External APIs Tab */}
        <TabsContent value="apis" className="space-y-6">
          <ApisTab
            apiConnections={apiConnections}
            setApiConnections={setApiConnections}
            updateApiKey={updateApiKey}
          />
        </TabsContent>
      </Tabs>

      {/* Change Plan Modal */}
      <Dialog open={showChangePlanModal} onOpenChange={setShowChangePlanModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Change Your Plan</DialogTitle>
            <DialogDescription>
              {selectedNewPlan && PLANS.findIndex(p => p.id === selectedNewPlan) > PLANS.findIndex(p => p.id === currentPlan)
                ? 'Upgrade to get more features and capabilities'
                : 'Downgrade your plan - changes will apply at the end of your current billing cycle'}
            </DialogDescription>
          </DialogHeader>

          {selectedNewPlan && (
            <div className="py-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Current Plan</p>
                  <p className="font-heading font-bold">{currentPlanData?.name} - ${currentPlanData?.price}/mo</p>
                </div>
                <ArrowDownRight className="w-5 h-5 text-muted-foreground" />
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">New Plan</p>
                  <p className="font-heading font-bold">{PLANS.find(p => p.id === selectedNewPlan)?.name} - ${PLANS.find(p => p.id === selectedNewPlan)?.price}/mo</p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                {PLANS.findIndex(p => p.id === selectedNewPlan) > PLANS.findIndex(p => p.id === currentPlan)
                  ? 'Your new plan will be activated immediately. You will be charged the prorated difference.'
                  : 'Your plan will be downgraded at the end of your current billing cycle.'}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePlanModal(false)}>Cancel</Button>
            <Button onClick={handleChangePlan} disabled={saving} className="bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive">Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your current billing period.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
              <h4 className="font-medium text-destructive mb-2">You will lose access to:</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Advanced risk assessment features</li>
                <li>• AI-powered summaries</li>
                <li>• Priority support</li>
                <li>• All API integrations</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>Keep Subscription</Button>
            <Button variant="destructive" onClick={handleCancelSubscription} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Cancel Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
